import React, { useState } from "react";

const AUTH_API = process.env.REACT_APP_AUTH_API ?? "";

interface AuthFormProps {
  onAuthenticated: (email: string, name: string) => void;
}

type AuthStep = "register" | "otp";

const AuthForm: React.FC<AuthFormProps> = ({ onAuthenticated }) => {
  const [step, setStep] = useState<AuthStep>("register");
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [mobile, setMobile] = useState("");
  const [otp, setOtp] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const handleRegister = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setLoading(true);

    try {
      const resp = await fetch(`${AUTH_API}/register`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, name, mobile }),
      });
      const data = await resp.json();

      if (!resp.ok) {
        setError(data.error || "Registration failed");
        return;
      }

      if (data.requiresMFA) {
        setStep("otp");
      }
    } catch (err: any) {
      setError(err.message || "Network error");
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOtp = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setLoading(true);

    try {
      const resp = await fetch(`${AUTH_API}/verify-mfa`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, mfaCode: otp }),
      });
      const data = await resp.json();

      if (!resp.ok) {
        setError(data.error || "Verification failed");
        return;
      }

      if (data.success) {
        localStorage.setItem("hw_user_email", email);
        localStorage.setItem("hw_user_name", data.user?.name || name);
        localStorage.setItem("hw_auth_verified", "true");
        onAuthenticated(email, data.user?.name || name);
      }
    } catch (err: any) {
      setError(err.message || "Network error");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="auth-form">
      <div className="auth-card">
        <div className="auth-header">
          <h2>Agentic EDA</h2>
          <p>Sign in to generate manufacturing-ready KiCad files</p>
        </div>

        {step === "register" && (
          <form onSubmit={handleRegister}>
            <div className="auth-field">
              <label htmlFor="auth-name">Full Name</label>
              <input
                id="auth-name"
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="John Doe"
                required
              />
            </div>
            <div className="auth-field">
              <label htmlFor="auth-email">Email</label>
              <input
                id="auth-email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@company.com"
                required
              />
            </div>
            <div className="auth-field">
              <label htmlFor="auth-mobile">Mobile (optional)</label>
              <input
                id="auth-mobile"
                type="tel"
                value={mobile}
                onChange={(e) => setMobile(e.target.value)}
                placeholder="+1 234 567 8900"
              />
            </div>
            <button type="submit" className="auth-btn" disabled={loading}>
              {loading ? "Sending OTP..." : "Get Started →"}
            </button>
          </form>
        )}

        {step === "otp" && (
          <form onSubmit={handleVerifyOtp}>
            <p className="auth-otp-msg">
              We've sent a 6-digit code to <strong>{email}</strong>
            </p>
            <div className="auth-field">
              <label htmlFor="auth-otp">Verification Code</label>
              <input
                id="auth-otp"
                type="text"
                inputMode="numeric"
                maxLength={6}
                value={otp}
                onChange={(e) => setOtp(e.target.value.replace(/\D/g, ""))}
                placeholder="123456"
                autoFocus
                required
              />
            </div>
            <button type="submit" className="auth-btn" disabled={loading || otp.length !== 6}>
              {loading ? "Verifying..." : "Verify & Sign In"}
            </button>
            <button
              type="button"
              className="auth-back-btn"
              onClick={() => { setStep("register"); setOtp(""); setError(""); }}
            >
              ← Back
            </button>
          </form>
        )}

        {error && <div className="auth-error">{error}</div>}
      </div>
    </div>
  );
};

export default AuthForm;
